import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Form, Input, Button, Skeleton, Upload, Image } from "antd";
import toast from "react-hot-toast";
import Edit from "../../../assets/Edit";
import {
  getSiteSettingsApi,
  UpdateSiteSettingsApi,
} from "../../../api/request/setting/setting";
import LoadableButton from "../../button/LoadableButton";
import GlobalTooltip from "../../common/GlobalTooltip";

const SiteSettings = () => {
  const userDetails = useSelector((state) => state.user.userDetails);
  const [form] = Form.useForm();
  const [settingsData, setSettingsData] = useState({});
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isUpdateLoading, setIsUpdateLoading] = useState(false);

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState("");
  const [fileList, setFileList] = useState([]);

  const isAdmin = userDetails?.role === "admin";

  const getBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });

  const handlePreview = async (file) => {
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj);
    }
    setPreviewImage(file.url || file.preview);
    setPreviewOpen(true);
  };

  const handleChange = ({ fileList: newFileList }) => {
    setFileList(newFileList);
    form.setFieldsValue({ logo: newFileList });
    if (!newFileList?.length) {
      form.setFieldsValue({ logo: [] });
    }
  };

  const UploadButton = (
    <button type="button" className="flex flex-col items-center gap-2">
      <div className="size-7">
        <img src="/assets/icons/upload.svg" alt="icon" />
      </div>
      Upload
    </button>
  );

  const fields = [
    { name: "business_name", label: "Business Name", type: "text" },
    { name: "gst_no", label: "GST Number", type: "text" },
    { name: "e_transfer_email", label: "E-Transfer Email", type: "email" },
    { name: "phone", label: "Phone", type: "phone" },
    { name: "email", label: "Email", type: "email" },
    { name: "address", label: "Address", type: "text" },
    { name: "address_url", label: "Address URL", type: "url" },
    { name: "website_url", label: "Website URL", type: "url" },
    { name: "facebook_url", label: "Facebook URL", type: "url" },
    { name: "instagram_url", label: "Instagram URL", type: "url" },
    { name: "twitter_url", label: "Twitter URL", type: "url" },
    { name: "linkedin_url", label: "Linkedin URL", type: "url" },
    { name: "playstore_url", label: "Playstore URL", type: "url" },
    { name: "applestore_url", label: "Applestore URL", type: "url" },
    { name: "gtm_container_id", label: "GTM Container Id", type: "text" },
  ];

  const validation = {
    phone: [
      { pattern: /^\+?[\d\s()-]+$/, message: "Enter a valid phone number" },
    ],
    email: [{ type: "email", message: "Enter a valid email" }],
    url: [{ type: "url", message: "Enter a valid URL" }],
  };

  const getSiteSettings = async () => {
    try {
      setIsLoading(true);
      const { data } = await getSiteSettingsApi();
      if (data?.status) {
        if (data?.data?.logo) {
          setFileList([{ uid: "1", url: data?.data?.logo, status: "done" }]);
        }
        setSettingsData(data?.data);
        setIsLoading(false);
      }
    } catch (error) {
      setIsLoading(false); 
    }
  };

  useEffect(() => {
    getSiteSettings();
  }, []);

  const handleSave = async (values) => {
    setIsUpdateLoading(true);
    try {
      const apiHeader = {
        headers: {
          Accept: "application/json",
          "Content-Type": "multipart/form-data",
        },
      };
      if (values?.logo?.length) values.logo = values?.logo?.[0]?.originFileObj;
      const { data } = await UpdateSiteSettingsApi(values, apiHeader);
      if (data?.status) {
        setIsEditing(false);
        toast.success(data?.message);
        setSettingsData({ ...settingsData, ...data?.data });
      }
    } catch (error) {
      if (error?.response?.data?.message)
        toast.error(error?.response?.data?.message);
    } finally {
      setIsUpdateLoading(false);
    }
  };

  return (
    <div className="h-full border rounded-lg p-4 lg:mt-0 mt-5">
      <div className="flex justify-between border-b pb-3">
        <p className="title">Site Settings</p>
        {isAdmin ? (
          <GlobalTooltip title={"Edit Site Settings"}>
            <button onClick={() => setIsEditing(!isEditing)}>
              <Edit />
            </button>
          </GlobalTooltip>
        ) : null}
      </div>

      <div className="pt-4">
        {isEditing ? (
          <Form
            form={form}
            layout="vertical"
            initialValues={settingsData}
            onFinish={handleSave}
          >
            <div className="max-h-[550px] overflow-y-scroll">
              <div>
                <Form.Item name="logo" label="Business Logo">
                  <Upload
                    beforeUpload={() => false}
                    multiple
                    listType="picture-card"
                    fileList={fileList}
                    onPreview={handlePreview}
                    onChange={handleChange}
                  >
                    {fileList.length >= 1 ? null : UploadButton}
                  </Upload>
                </Form.Item>
                {previewImage && (
                  <Image
                    wrapperStyle={{ display: "none" }}
                    preview={{
                      visible: previewOpen,
                      onVisibleChange: (visible) => setPreviewOpen(visible),
                      afterOpenChange: (visible) =>
                        !visible && setPreviewImage(""),
                    }}
                    src={previewImage}
                  />
                )}
              </div>

              {fields.map((field) => (
                <Form.Item
                  key={field.name}
                  name={field.name}
                  label={field.label}
                  rules={validation[field.type] || []}
                >
                  <Input placeholder={`Enter ${field.label}`} />
                </Form.Item>
              ))}
            </div>

            <div className="flex items-center gap-3 pt-4">
              <LoadableButton
                type="submit"
                lable="Update"
                loadingLable="Updating..."
                isLoading={isUpdateLoading}
              />
              {/* <button
                className="btn-outlined"
                onClick={() => setIsEditing(false)}
              >
                Cancel
              </button> */}

              <LoadableButton
                lable="Cancel"
                className="!bg-white !text-secondary border !border-secondary"
                onClick={() => setIsEditing(false)}
              />
            </div>
          </Form>
        ) : (
          <div className="space-y-4">
            {isLoading ? (
              fields.map((field) => (
                <div key={field.name} className="flex gap-3">
                  <Skeleton.Input active size="small" />
                  <Skeleton.Input active size="small" className="!w-[300px]" />
                </div>
              ))
            ) : (
              <>
                <div className="flex gap-3">
                  <span className="font-medium">Business Logo:</span>
                  {settingsData?.logo ? (
                    <a
                      href={settingsData.logo}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-500"
                    >
                      View
                    </a>
                  ) : (
                    "--"
                  )}
                </div>

                {fields.map((field) => {
                  const value = settingsData[field?.name];
                  return (
                    <div key={field.name} className="flex gap-3">
                      <span className="font-medium">{field.label}:</span>
                      {field.type === "url" && value ? (
                        <a
                          href={value}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-500 line-clamp-2 text-ellipsis"
                        >
                          {value}
                        </a>
                      ) : (
                        value || "--"
                      )}
                    </div>
                  );
                })}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SiteSettings;
